import React from 'react';
import { useNavigate } from 'react-router-dom';
import './UserTable.css';

function UserTable({ users, onDisable, onEnable, onResetPassword, loading, selectedUsers = [], onSelectUser, onSelectAll }) {
  const navigate = useNavigate();
  const hasSelection = typeof onSelectUser === 'function';

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return <div className="user-table-loading">Loading users...</div>;
  }

  if (!users || users.length === 0) {
    return <div className="user-table-empty">No users found</div>;
  }

  const handleKeyDown = (e, action, userId) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (action === 'view') {
        navigate(`/admin/users/${userId}`);
      } else if (action === 'reset') {
        onResetPassword(userId);
      } else if (action === 'disable') {
        onDisable(userId);
      } else if (action === 'enable') {
        onEnable(userId);
      }
    }
  };

  return (
    <div className="user-table-container" role="region" aria-label="Users table">
      <table className="user-table" role="table" aria-label="Registered users list">
        <thead>
          <tr>
            {hasSelection && (
              <th scope="col" className="col-checkbox">
                <input
                  type="checkbox"
                  checked={users.length > 0 && selectedUsers.length === users.length}
                  onChange={(e) => onSelectAll(e.target.checked)}
                  aria-label="Select all users"
                />
              </th>
            )}
            <th scope="col">ID</th>
            <th scope="col">Email</th>
            <th scope="col">Name</th>
            <th scope="col">Phone</th>
            <th scope="col">Registration</th>
            <th scope="col">Status</th>
            <th scope="col">Features</th>
            <th scope="col">WhatsApp</th>
            <th scope="col">Last Login</th>
            <th scope="col">Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.id}>
              {hasSelection && (
                <td className="col-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedUsers.includes(user.id)}
                    onChange={(e) => onSelectUser(user.id, e.target.checked)}
                    aria-label={`Select user ${user.email}`}
                  />
                </td>
              )}
              <td>{user.id}</td>
              <td>{user.email}</td>
              <td>{user.first_name} {user.last_name}</td>
              <td>{user.phone_number || '-'}</td>
              <td>{formatDate(user.registration_date)}</td>
              <td>
                {/* Show subscription status badge */}
                <span 
                  className={`status-badge ${user.subscription_status}`}
                  aria-label={`Subscription status: ${user.subscription_status}`}
                >
                  {user.subscription_status}
                </span>
                {/* For trial users, also show active badge to indicate they're active */}
                {user.subscription_status === 'trial' && user.is_active && (
                  <span 
                    className="status-badge active"
                    aria-label="Account status: active"
                  >
                    Active
                  </span>
                )}
                {/* Don't show additional inactive badge if subscription_status is already "inactive" 
                    (this prevents double "inactive" labels) */}
              </td>
              <td>
                {(() => {
                  const flags = user.feature_flags || {};
                  const masterOff = !flags.can_connect_socials;
                  return (
                    <div className="feature-badges" title={`Socials: ${flags.can_connect_socials ? 'on' : 'off'}, IG: ${flags.can_connect_instagram ? 'on' : 'off'}, FB: ${flags.can_connect_facebook ? 'on' : 'off'}, TT: ${flags.can_connect_tiktok ? 'on' : 'off'}, Posts: ${flags.can_generate_posts ? 'on' : 'off'}`}>
                      <span className={`feature-dot ${flags.can_connect_socials ? 'on' : 'off'}`} title="Connect Socials (master)">🔗</span>
                      <span className={`feature-dot ${!masterOff && flags.can_connect_instagram ? 'on' : 'off'}`} title="Instagram">📸</span>
                      <span className={`feature-dot ${!masterOff && flags.can_connect_facebook ? 'on' : 'off'}`} title="Facebook">📘</span>
                      <span className={`feature-dot ${!masterOff && flags.can_connect_tiktok ? 'on' : 'off'}`} title="TikTok">🎵</span>
                      <span className={`feature-dot ${flags.can_generate_posts ? 'on' : 'off'}`} title="Generate Posts">✨</span>
                    </div>
                  );
                })()}
              </td>
              <td>{user.whatsapp_numbers_count || 0}</td>
              <td>{formatDate(user.last_login)}</td>
              <td>
                <div className="action-buttons" role="group" aria-label={`Actions for user ${user.email}`}>
                  <button
                    className="btn-view"
                    onClick={() => navigate(`/admin/users/${user.id}`)}
                    onKeyDown={(e) => handleKeyDown(e, 'view', user.id)}
                    aria-label={`View details for user ${user.email}`}
                  >
                    View
                  </button>
                  <button
                    className="btn-reset"
                    onClick={() => onResetPassword(user.id)}
                    onKeyDown={(e) => handleKeyDown(e, 'reset', user.id)}
                    aria-label={`Reset password for user ${user.email}`}
                  >
                    Reset
                  </button>
                  {user.is_active ? (
                    <button
                      className="btn-disable"
                      onClick={() => onDisable(user.id)}
                      onKeyDown={(e) => handleKeyDown(e, 'disable', user.id)}
                      aria-label={`Disable user ${user.email}`}
                    >
                      Disable
                    </button>
                  ) : (
                    <button
                      className="btn-enable"
                      onClick={() => onEnable(user.id)}
                      onKeyDown={(e) => handleKeyDown(e, 'enable', user.id)}
                      aria-label={`Enable user ${user.email}`}
                    >
                      Enable
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default UserTable;

